import { useState, FormEvent, ChangeEvent } from 'react';
import {
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Paper,
  Typography,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  CircularProgress,
  Alert
} from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';

export interface FormData {
  guestTitle: string;
  guestName: string;
  email: string;
}

interface FeedbackFormProps {
  onSubmit: (data: FormData) => Promise<void>;
}

const TITLE_OPTIONS = [
  'Mr.', 'Mrs.', 'Miss.', 'Ms.', 'Dr.', 'Drs.', 'Sen.', 'The Honorable', 'Other'
];

const EMPTY: FormData = { guestTitle: '', guestName: '', email: '' };

export default function FeedbackForm({ onSubmit }: FeedbackFormProps) {
  const [formData, setFormData] = useState<FormData>(EMPTY);
  const [errors, setErrors] = useState({ guestTitle: false, guestName: false, email: false });
  const [openConfirm, setOpenConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [customTitleDialog, setCustomTitleDialog] = useState(false);
  const [customTitleInput, setCustomTitleInput] = useState('');

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleTitleChange = (e: SelectChangeEvent<string>) => {
    if (e.target.value === 'Other') {
      setCustomTitleDialog(true);
    } else {
      setFormData({ ...formData, guestTitle: e.target.value });
    }
  };

  const confirmCustomTitle = () => {
    if (customTitleInput.trim()) {
      setFormData({ ...formData, guestTitle: customTitleInput.trim() });
    }
    setCustomTitleDialog(false);
    setCustomTitleInput('');
  };

  const validate = (): boolean => {
    const next = {
      guestTitle: formData.guestTitle.trim() === '',
      guestName: formData.guestName.trim() === '',
      email: !/\S+@\S+\.\S+/.test(formData.email)
    };
    setErrors(next);
    return !Object.values(next).some(Boolean);
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    if (validate()) setOpenConfirm(true);
  };

  const handleConfirm = async () => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      await onSubmit(formData);
      setOpenConfirm(false);
      setFormData(EMPTY);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'An unknown error occurred');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Paper elevation={3} className="form-paper">
      <Typography variant="h5" component="h1" className="form-title">
        Guest Comment Card Request
      </Typography>

      <form onSubmit={handleSubmit}>
        <FormControl fullWidth margin="normal" error={errors.guestTitle} className="form-field">
          <InputLabel id="title-label">Title</InputLabel>
          <Select
            labelId="title-label"
            id="guestTitle"
            name="guestTitle"
            value={formData.guestTitle}
            onChange={handleTitleChange}
            label="Title"
          >
            {TITLE_OPTIONS.map((t) => (
              <MenuItem key={t} value={t}>{t}</MenuItem>
            ))}
          </Select>
          {errors.guestTitle && <FormHelperText>Please select a title</FormHelperText>}
        </FormControl>

        <TextField
          fullWidth
          margin="normal"
          id="guestName"
          name="guestName"
          label="Last Name"
          variant="outlined"
          value={formData.guestName}
          onChange={handleChange}
          error={errors.guestName}
          helperText={errors.guestName ? 'Please enter your last name' : ''}
          className="form-field"
        />

        <TextField
          fullWidth
          margin="normal"
          id="email"
          name="email"
          label="Email Address"
          variant="outlined"
          type="email"
          value={formData.email}
          onChange={handleChange}
          error={errors.email}
          helperText={errors.email ? 'Please enter a valid email address' : ''}
          className="form-field"
          inputProps={{ inputMode: 'email', autoComplete: 'email' }}
        />

        <Box sx={{ mt: 4 }}>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            size="large"
            className="submit-button"
            disabled={submitting}
          >
            Send Feedback Request
          </Button>
        </Box>
      </form>

      <Dialog open={openConfirm} onClose={() => !submitting && setOpenConfirm(false)}>
        <DialogTitle>Confirm Email</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Send a feedback request email to {formData.guestTitle} {formData.guestName} at {formData.email}?
          </DialogContentText>
          {submitError && <Alert severity="error" sx={{ mt: 2 }}>{submitError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenConfirm(false)} color="primary" disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            color="primary"
            variant="contained"
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {submitting ? 'Sending...' : 'Send Email'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={customTitleDialog} onClose={() => setCustomTitleDialog(false)}>
        <DialogTitle>Enter Custom Title</DialogTitle>
        <DialogContent>
          <DialogContentText>Please enter your custom title:</DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="Title"
            fullWidth
            variant="outlined"
            value={customTitleInput}
            onChange={(e) => setCustomTitleInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') confirmCustomTitle(); }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCustomTitleDialog(false)}>Cancel</Button>
          <Button onClick={confirmCustomTitle} variant="contained" disabled={!customTitleInput.trim()}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
